import { ChangeEvent, useState, useMemo, useCallback } from 'react';
import { JobCountDetailResponse } from '@doda/api-client/aspida/@types';
import { findJobCount } from 'components/OccupationSelectModal/logics/createOccupationDisplayData';
import { RouteStationDrillDownType } from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/DrillDown/RouteStationDrillDown';
import { useAppSelector } from 'redux/hooks';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { InitialRouteStationDataType } from '../types';

interface ArgumentType {
  initialRouteStationData: InitialRouteStationDataType;
  initialRouteStationDrillDown?: RouteStationDrillDownType[];
}

/**
 * 路線駅に関するhooks
 * @param param.initialRouteStationData 整形された路線駅マスタ
 * @param param.initialRouteStationDrillDown ドリルダウン
 * @returns 表示用配列と更新処理
 */
export const useRouteStation = ({
  initialRouteStationData,
  initialRouteStationDrillDown,
}: ArgumentType) => {
  const { preBtn, st } = useAppSelector(appQueryParamsSelector);
  const isDrillDown = preBtn === '1';
  const [routeList, setRouteList] = useState(
    initialRouteStationData?.routeData ?? []
  );
  const [stationList, setStationList] = useState(
    initialRouteStationData?.stationData ?? []
  );

  const routeStationDisplayList = useMemo(() => {
    return routeList.map((route) => ({
      ...route,
      stationList: stationList.filter(({ routeId }) => routeId === route.id),
    }));
  }, [routeList, stationList]);

  // 選択中の路線のid配列
  const selectedRouteIdList = useMemo(() => {
    return routeList.filter(({ isChecked }) => isChecked).map(({ id }) => id);
  }, [routeList]);

  const selectedStationIdList = useMemo(() => {
    return stationList.filter(({ isChecked }) => isChecked).map(({ id }) => id);
  }, [stationList]);

  // 路線のチェックボックス更新処理
  const updateIsCheckedRouteList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = routeList.map((route) => {
        if (route.id !== selectedId) return route;

        return {
          ...route,
          isChecked: event.target.checked,
        };
      });

      setRouteList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ id }) => id);
    },
    [routeList]
  );

  // 駅のチェックボックス更新処理
  const updateIsCheckedStationList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = stationList.map((station) => {
        if (station.id !== selectedId) return station;

        return {
          ...station,
          isChecked: event.target.checked,
        };
      });

      setStationList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ id }) => id);
    },
    [stationList]
  );

  const updateRouteListJobCount = useCallback(
    (routeIdList: JobCountDetailResponse[] | undefined) => {
      setRouteList((pre) =>
        pre.map((route) => {
          // チェックが入っていない場合は更新しない
          if (!route.isChecked) return route;

          return {
            ...route,
            jobCount: findJobCount(routeIdList, route.id),
          };
        })
      );
    },
    []
  );

  const updateStationListJobCount = useCallback(
    (stationIdList: JobCountDetailResponse[] | undefined) => {
      setStationList((pre) =>
        pre.map((station) => {
          // チェックが入っていない場合は更新しない
          if (!station.isChecked) return station;

          return {
            ...station,
            jobCount: findJobCount(stationIdList, station.id),
          };
        })
      );
    },
    []
  );

  const [routeStationDrillDown, setRouteStationDrillDown] = useState(
    initialRouteStationDrillDown ?? []
  );

  /**
   * URLが路線の場合（/DodaFront/View/JobSearchList/j_rt__J002001/-preBtn__1/）の
   * 初期選択状態（駅のチェックボックスが更新されていない場合）
   */
  const selectedRouteIdDrillDown = routeStationDrillDown
    .filter(({ isChecked }) => isChecked)
    .map(({ id }) => id);

  /**
   * 1.URLに駅のクエリがある場合はクエリを使用（/DodaFront/View/JobSearchList/j_st__22828/-preBtn__1/）
   * 2.そうでない場合はドリルダウンのチェックボックスからidを取得する（/DodaFront/View/JobSearchList/j_rt__J002001/-preBtn__1/）
   */
  const selectedStationDrillDown =
    st ||
    routeStationDrillDown
      ?.flatMap(({ stations }) => stations.filter(({ isChecked }) => isChecked))
      .map(({ id }) => id);

  // ドリルダウンのチェックボックス更新処理
  const updateStationDrillDownIsChecked = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      if (initialRouteStationDrillDown && routeStationDrillDown) {
        const checkedList = routeStationDrillDown.map((route) => {
          const stations = route.stations.map((station) => {
            if (station.id !== selectedId) return station;

            return {
              ...station,
              isChecked: event.target.checked,
            };
          });

          return {
            ...route,
            isChecked: stations.every((item) => !item.isChecked),
            stations,
          };
        });

        setRouteStationDrillDown(checkedList);

        return {
          routeId: checkedList
            .filter(({ isChecked }) => isChecked)
            .map(({ id }) => id),
          stationId: checkedList
            .flatMap(({ stations }) =>
              stations.filter(({ isChecked }) => isChecked)
            )
            .map(({ id }) => id),
        };
      }

      return undefined;
    },
    [initialRouteStationDrillDown, routeStationDrillDown]
  );

  const updateRouteStationJobCount = (
    route: JobCountDetailResponse[] | undefined,
    station: JobCountDetailResponse[] | undefined
  ) => {
    updateRouteListJobCount(route);
    updateStationListJobCount(station);
  };

  return {
    routeStationDisplayList,
    routeStationDrillDown,
    selectedRouteIdList: isDrillDown
      ? selectedRouteIdDrillDown
      : selectedRouteIdList,
    selectedStationIdList: isDrillDown
      ? selectedStationDrillDown
      : selectedStationIdList,
    updateIsCheckedRouteList,
    updateIsCheckedStationList,
    updateRouteStationJobCount,
    updateStationDrillDownIsChecked,
  };
};
